import { db } from "../config";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  increment,
  writeBatch,
  orderBy,
} from "firebase/firestore";
import { trackFollowUser, trackUnfollowUser } from "./analyticsServices";
import { createNotification } from "./notificationServices";

const USER_FOLLOWS_COLLECTION = "UserFollows";
const FOLLOW_REQUESTS_COLLECTION = "FollowRequests";

export const followUser = async (followerId, followedId) => {
  try {
    // Get follower data first
    const followerRef = doc(db, "Users", followerId);
    const followerSnap = await getDoc(followerRef);
    const followerData = followerSnap.data();

    if (!followerData) {
      throw new Error("Follower data not found");
    }

    const batch = writeBatch(db);

    // Create follow document
    const followRef = doc(
      db,
      USER_FOLLOWS_COLLECTION,
      `${followerId}_${followedId}`
    );
    batch.set(followRef, {
      followerId,
      followedId,
      createdAt: new Date().toISOString(),
    });

    // Update follower count for followed user
    const followedUserRef = doc(db, "Users", followedId);
    batch.update(followedUserRef, {
      followers: increment(1),
    });

    // Update following count for follower
    const followerUserRef = doc(db, "Users", followerId);
    batch.update(followerUserRef, {
      following: increment(1),
    });

    await batch.commit();

    // Create notification after batch commit
    await createNotification({
      userId: followedId,
      type: "NEW_FOLLOWER",
      sourceId: followerId,
      sourceType: "user",
      message: `${followerData.username} started following you`,
      metadata: {
        actorId: followerId,
        actorName: followerData.username,
        actorImage: followerData.profileImage || null,
      },
    });

    // Track follow action
    trackFollowUser(followerId, followedId);
  } catch (error) {
    console.error("Error following user:", error);
    throw error;
  }
};

export const unfollowUser = async (followerId, followedId) => {
  try {
    const batch = writeBatch(db);

    // Delete follow document
    const followRef = doc(
      db,
      USER_FOLLOWS_COLLECTION,
      `${followerId}_${followedId}`
    );
    batch.delete(followRef);

    // Update follower count for followed user
    const followedUserRef = doc(db, "Users", followedId);
    batch.update(followedUserRef, {
      followers: increment(-1),
    });

    // Update following count for follower
    const followerUserRef = doc(db, "Users", followerId);
    batch.update(followerUserRef, {
      following: increment(-1),
    });

    await batch.commit();

    // Track unfollow action
    trackUnfollowUser(followerId, followedId);
  } catch (error) {
    console.error("Error unfollowing user:", error);
    throw error;
  }
};

export const checkFollowStatus = async (followerId, followedId) => {
  try {
    const followRef = doc(
      db,
      USER_FOLLOWS_COLLECTION,
      `${followerId}_${followedId}`
    );
    const followDoc = await getDoc(followRef);
    return followDoc.exists();
  } catch (error) {
    console.error("Error checking follow status:", error);
    throw error;
  }
};

export const getFollowers = async (userId, page = 0, pageSize = 10) => {
  try {
    const q = query(
      collection(db, USER_FOLLOWS_COLLECTION),
      where("followedId", "==", userId),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const allDocs = querySnapshot.docs;

    // Manual pagination
    const start = page * pageSize;
    const paginatedDocs = allDocs.slice(start, start + pageSize);

    const followers = await Promise.all(
      paginatedDocs.map(async (followDoc) => {
        const followerDoc = await getDoc(
          doc(db, "Users", followDoc.data().followerId)
        );
        return {
          uid: followerDoc.id,
          ...followerDoc.data(),
          followDate: followDoc.data().createdAt,
        };
      })
    );

    return followers;
  } catch (error) {
    console.error("Error getting followers:", error);
    throw error;
  }
};

export const getFollowing = async (userId, page = 0, pageSize = 10) => {
  try {
    const q = query(
      collection(db, USER_FOLLOWS_COLLECTION),
      where("followerId", "==", userId),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const allDocs = querySnapshot.docs;

    // Manual pagination
    const start = page * pageSize;
    const paginatedDocs = allDocs.slice(start, start + pageSize);

    const following = await Promise.all(
      paginatedDocs.map(async (followDoc) => {
        const followedDoc = await getDoc(
          doc(db, "Users", followDoc.data().followedId)
        );
        return {
          uid: followedDoc.id,
          ...followedDoc.data(),
          followDate: followDoc.data().createdAt,
        };
      })
    );

    return following;
  } catch (error) {
    console.error("Error getting following:", error);
    throw error;
  }
};

export const sendFollowRequest = async (requesterId, targetUserId) => {
  try {
    // Get requester data first
    const requesterRef = doc(db, "Users", requesterId);
    const requesterSnap = await getDoc(requesterRef);
    const requesterData = requesterSnap.data();

    if (!requesterData) {
      throw new Error("Requester data not found");
    }

    const batch = writeBatch(db);

    // Create follow request document
    const requestRef = doc(
      db,
      FOLLOW_REQUESTS_COLLECTION,
      `${requesterId}_${targetUserId}`
    );
    batch.set(requestRef, {
      requesterId,
      targetUserId,
      status: "pending",
      createdAt: new Date().toISOString(),
    });

    await batch.commit();

    // Create notification after batch commit
    await createNotification({
      userId: targetUserId,
      type: "FOLLOW_REQUEST",
      sourceId: requesterId,
      sourceType: "user",
      message: `${requesterData.username} requested to follow you`,
      metadata: {
        actorId: requesterId,
        actorName: requesterData.username,
        actorImage: requesterData.profileImage || null,
      },
    });

    return true;
  } catch (error) {
    console.error("Error sending follow request:", error);
    throw error;
  }
};

export const checkFollowRequestStatus = async (requesterId, targetUserId) => {
  try {
    const requestRef = doc(
      db,
      FOLLOW_REQUESTS_COLLECTION,
      `${requesterId}_${targetUserId}`
    );
    const requestDoc = await getDoc(requestRef);
    return requestDoc.exists();
  } catch (error) {
    console.error("Error checking follow request status:", error);
    return false;
  }
};

export const cancelFollowRequest = async (requesterId, targetUserId) => {
  try {
    const batch = writeBatch(db);

    // Delete the follow request
    const requestRef = doc(
      db,
      FOLLOW_REQUESTS_COLLECTION,
      `${requesterId}_${targetUserId}`
    );
    batch.delete(requestRef);

    await batch.commit();
    return true;
  } catch (error) {
    console.error("Error canceling follow request:", error);
    throw error;
  }
};

export const handleFollowRequest = async (
  requesterId,
  targetUserId,
  accept
) => {
  try {
    const batch = writeBatch(db);

    // Find and update the notification
    const notificationsRef = collection(db, "Notifications");
    const q = query(
      notificationsRef,
      where("type", "==", "FOLLOW_REQUEST"),
      where("sourceId", "==", requesterId),
      where("userId", "==", targetUserId)
    );
    const notificationSnapshot = await getDocs(q);

    if (!notificationSnapshot.empty) {
      const notificationDoc = notificationSnapshot.docs[0];
      batch.update(notificationDoc.ref, {
        read: true,
        metadata: {
          ...notificationDoc.data().metadata,
          handled: true,
          accepted: accept,
        },
      });
    }

    if (accept) {
      // Create the follow relationship
      const followRef = doc(
        db,
        USER_FOLLOWS_COLLECTION,
        `${requesterId}_${targetUserId}`
      );
      batch.set(followRef, {
        followerId: requesterId,
        followedId: targetUserId,
        createdAt: new Date().toISOString(),
      });

      // Update follower counts
      const targetUserRef = doc(db, "Users", targetUserId);
      const requesterRef = doc(db, "Users", requesterId);

      batch.update(targetUserRef, {
        followers: increment(1),
      });
      batch.update(requesterRef, {
        following: increment(1),
      });

      // Create notification for accepted request
      await createNotification({
        userId: requesterId,
        type: "FOLLOW_REQUEST_ACCEPTED",
        sourceId: targetUserId,
        sourceType: "user",
        message: `Your follow request was accepted`,
        metadata: {
          actorId: targetUserId,
        },
      });
    }

    // Delete the follow request
    const requestRef = doc(
      db,
      FOLLOW_REQUESTS_COLLECTION,
      `${requesterId}_${targetUserId}`
    );
    batch.delete(requestRef);

    await batch.commit();
    return true;
  } catch (error) {
    console.error("Error handling follow request:", error);
    throw error;
  }
};
